import * as React from 'react';

import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import AppState from '@webapp/store/state';
import { Release } from '@common/types/release/release';
import { UpdateStatus } from '@webapp/types/update-status';

import styled from 'styled-components';

import Button from '@components/actions/button';
import Typography from '@components/basics/typography';
import NavbarItemAvatarCtrl from '../navbar-items/navbar-item-avatar';
import { Dispatch } from 'redux';
import {
  CleanRelease,
  ConfirmTakedownRelease,
  ConfirmCancelReleaseChanges,
} from '../../../../store/release/action-creators';
import { PublishRelease } from '../../../../store/release/action-creators';
import { useLocation } from 'react-router';
import { Translate } from 'react-localize-redux';

interface NavbarActionsGeneralCtrlProps {
  className?: string;
  updateStatus: UpdateStatus;
  isMobile: true;
  canDelete: boolean;
  canCancelChanges: boolean;
  artistId: string;
  releaseId: string;
  release?: Partial<Release>;
  releaseTitle: string;
  artistName: string;
  takeDown: (
    artistId: string,
    releaseId: string,
    title: string,
    isDelete: boolean
  ) => ConfirmTakedownRelease;
  cancelChanges: (
    artistId: string,
    releaseId: string
  ) => ConfirmCancelReleaseChanges;
  finishLater: (artistId: string, artistName: string) => CleanRelease;
  publish: (
    artistId: string,
    releaseId: string,
    release: Partial<Release>
  ) => PublishRelease;
}

const getRelevantStatus = (updateStatus: UpdateStatus[]): UpdateStatus => {
  if (updateStatus.every((s) => s === updateStatus[0])) {
    return updateStatus[0];
  }

  const status = updateStatus.filter((s) => s !== 'initial');

  if (!status.length) {
    return 'initial';
  }

  if (status.includes('modified')) {
    return 'modified';
  }

  return 'saved';
};

const mapStateToProps = (state: AppState) => ({
  updateStatus: getRelevantStatus([
    state.release.updateStatus,
    state.tracks.updateStatus,
    state.ownership.updateStatus,
    state.collaborator.updateStatus,
  ]),
  artistId: state.artist.active?.id,
  releaseId: state.release.release?.id,
  release: state.release.release,
  releaseTitle: state.release.release?.title,
  isMobile: state.ui.mobile,
  canDelete:
    state.release.release?.id &&
    ['draft', 'taken-down', 'changes-needed'].includes(
      state.release.release?.status || ''
    ),
  canCancelChanges:
    state.release.release?.id &&
    ['pending', 'approved', 'published'].includes(
      state.release.release?.status || ''
    ),
  artistName: state.artist.active?.name,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  takeDown: (
    artistId: string,
    releaseId: string,
    title: string,
    isDelete: boolean
  ) =>
    dispatch(new ConfirmTakedownRelease(artistId, releaseId, title, isDelete)),
  cancelChanges: (artistId: string, releaseId: string) =>
    dispatch(new ConfirmCancelReleaseChanges(releaseId, artistId)),
  finishLater: (artistId, artistName) =>
    dispatch(new CleanRelease(artistId, artistName)),
  publish: (artistId: string, releaseId: string, release: Partial<Release>) =>
    dispatch(new PublishRelease(artistId, releaseId, release)),
});

export const NavbarActionsReleaseCtrl: React.FC<
  NavbarActionsGeneralCtrlProps
> = (props) => {
  const getStatusText = () => {
    switch (props.updateStatus) {
      case 'modified':
        return 'NAVBAR.ACTIONS.RELEASE.SAVINGCHANGES';
      default:
        return 'NAVBAR.ACTIONS.RELEASE.FINISHLATER';
    }
  };

  const SaveAndPublish = () => {
    if (props.release) {
      props.publish(props.artistId, props.releaseId, props.release);
    }
  };
  const location = useLocation();

  const isPublished = location.pathname.endsWith('/published');

  const showButton =
    props.updateStatus !== 'initial' && !isPublished && !props.canCancelChanges;

  return (
    <div className={props.className}>
      <div className="release-navbar">
        <NavbarItemAvatarCtrl
          className="navbar-artist"
          onHeader
          noHover
          noIcons
        />
        {props.canDelete ? (
          <Button
            customHeight={40}
            isPillShape
            className="delete-button"
            buttonType="empty"
            small
            color={props.updateStatus === 'modified' ? 'red' : 'black'}
            onClick={() =>
              props.takeDown(
                props.artistId,
                props.releaseId,
                props.releaseTitle,
                props.canDelete
              )
            }
          >
            <Delete weight="700">Delete Release</Delete>
          </Button>
        ) : null}
        {props.canCancelChanges && props.updateStatus === 'modified' ? (
          <Button
            customHeight={40}
            isPillShape
            className="cancel-button"
            buttonType="empty"
            small
            onClick={() => props.cancelChanges(props.artistId, props.releaseId)}
          >
            <Cancel weight="700">Cancel</Cancel>
          </Button>
        ) : null}
        {props.canCancelChanges ? (
          <Button
            customHeight={40}
            small
            isPillShape
            buttonType="primary"
            disabled={!(props.updateStatus === 'modified')}
            onClick={SaveAndPublish}
          >
            <Typography className="item-title" weight="700">
              <Translate id="NAVBAR.ACTIONS.RELEASE.SAVE&PUBLISH" />
            </Typography>
          </Button>
        ) : null}
        {showButton ? (
          <Link to="/dashboard">
            <Button
              customHeight={40}
              small
              isPillShape
              buttonType="secondary"
              disabled={props.updateStatus === 'modified'}
              onClick={() =>
                props.finishLater(props.artistId, props.artistName)
              }
            >
              <Typography className="item-title" weight="700">
                <Translate id={getStatusText()} />
              </Typography>
            </Button>
          </Link>
        ) : null}
      </div>
    </div>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(NavbarActionsReleaseCtrl)`
  width: 100%;

  @media ${({ theme }) => theme.device.tablet} {
    width: auto;
  }

  .navbar-artist {
    margin-right: 30px;

    @media ${({ theme }) => theme.device.laptop} {
      margin-right: 148px;
    }
  }

  .delete-button {
    margin-right: 30px;
  }

  .cancel-button {
    margin-right: 30px;
  }

  .release-navbar {
    display: flex;
    align-items: center;

    .status {
      margin: 0 35px 0 9px;
    }
  }
`);

const Delete = styled(Typography)`
  color: ${({ theme }) => theme.colors.error};
`;
const Cancel = styled(Typography)``;
